// ClockPanel.jsx
import { useState, useEffect } from 'react';
import RadialProgress from './RadialProgress';
import StatCard from './StatCard';
import ShiftHistory from './ShiftHistory';
import LocationStatus from './LocationStatus';
import { LogInIcon, LogOutIcon } from 'lucide-react';
import axios from 'axios';

export default function ClockPanel() {
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentShiftMinutes, setCurrentShiftMinutes] = useState(0);
  const [shiftHistory, setShiftHistory] = useState([]);
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [locationVerified, setLocationVerified] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let interval;
    if (isClockedIn) {
      interval = setInterval(() => {
        setCurrentShiftMinutes(m => m + 1);
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [isClockedIn]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      () => setLocationVerified(true),
      () => setLocationVerified(false)
    );
  }, []);

  const formattedTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const formattedDate = now.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });

  const handleClockAction = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/attendance', {
        action: isClockedIn ? 'Check-out' : 'Check-in',
        timestamp: new Date().toISOString(),
      });
      if (response.data.success) {
        setIsClockedIn(!isClockedIn);
        setShiftHistory(prev => [
          {
            type: isClockedIn ? 'out' : 'in',
            time: formattedTime,
            date: formattedDate,
          },
          ...prev,
        ]);
      }
    } catch {
      // showToast('Action failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-white/70 to-white/40 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-6 flex flex-col gap-6">
      <div className="text-center py-6 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-xl mb-2">
        <div className="text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          {formattedTime}
        </div>
        <div className="text-xl text-gray-600 mt-2">{formattedDate}</div>
      </div>
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <RadialProgress value={currentShiftMinutes} max={480} size={120} thickness={8} color="bg-gradient-to-r from-emerald-500 to-teal-500" />
          <button
            className={`absolute inset-0 w-full h-full flex items-center justify-center py-4 rounded-xl text-white font-bold text-lg shadow-lg transition-all transform hover:scale-[1.02] ${
              isClockedIn
                ? 'bg-gradient-to-r from-red-500 to-amber-500 hover:shadow-red-200'
                : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:shadow-emerald-200'
            }`}
            onClick={handleClockAction}
            disabled={loading}
          >
            {isClockedIn ? (
              <div className="flex items-center justify-center gap-2">
                <LogOutIcon className="h-6 w-6" />
                Check Out
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <LogInIcon className="h-6 w-6" />
                Check In
              </div>
            )}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <StatCard title="Hours Today" value="5.2h" progress={65} color="from-blue-500 to-indigo-500" />
        <StatCard title="This Week" value="32.5h" progress={82} color="from-emerald-500 to-teal-500" />
      </div>
      <ShiftHistory shiftHistory={shiftHistory} />
      <LocationStatus verified={locationVerified} />
    </div>
  );
}
